// Display helpers for the Merlin Visibility Pane (no React) — the stream
// colour map shared by the badges, the summary bar and the topology-style
// legend, plus small row formatters. Kept out of the .tsx files so those export
// only components (react-refresh/only-export-components).

import type {
  ChannelBindingSummary,
  ChannelMessageRead,
  MonitorRow,
  StreamKind,
} from '../../services/merlinMonitor';

/** One tone per stream — the same family the Agent Topology page uses for its
 *  node kinds, so the two admin views agree. */
export const STREAM_TONE: Record<StreamKind, { bg: string; ink: string }> = {
  console: { bg: '#dbeafe', ink: '#1e40af' },
  quality_copilot: { bg: '#cffafe', ink: '#155e75' },
  tech_support: { bg: '#fce7f3', ink: '#9d174d' },
  portal_analyst: { bg: '#e0e7ff', ink: '#3730a3' },
  widget: { bg: '#ffedd5', ink: '#9a3412' },
  door: { bg: '#e0f2fe', ink: '#075985' },
  headless: { bg: '#fef3c7', ink: '#92400e' },
  pipeline: { bg: '#ede9fe', ink: '#5b21b6' },
  data_request: { bg: '#dcfce7', ink: '#166534' },
  retired: { bg: '#f1f5f9', ink: '#475569' },
  legacy: { bg: '#f1f5f9', ink: '#64748b' },
  unknown: { bg: '#fee2e2', ink: '#991b1b' },
};

export function streamTone(stream: string): { bg: string; ink: string } {
  return STREAM_TONE[stream as StreamKind] ?? STREAM_TONE.unknown;
}

/** Tooltip for a channel chip: medium, who, and what the thread is about. */
export function channelTitle(binding: ChannelBindingSummary): string {
  const who = binding.counterpart_label
    ? `${binding.counterpart_label} <${binding.counterpart_address}>`
    : binding.counterpart_address;
  return `${binding.channel.toUpperCase()} · ${who} · about ${binding.anchor_kind} ${binding.anchor_id}`;
}

/** What to call a row: its title, else the first thing the person asked. */
export function rowTitle(row: Pick<MonitorRow, 'title' | 'first_user_message'>): string {
  return row.title?.trim() || row.first_user_message?.trim() || '(untitled)';
}

/** A readable message for a failed request: the API's detail when it sent
 *  one, else the error's own message, else the caller's fallback. */
export function errorText(err: unknown, fallback: string): string {
  const maybe = err as { response?: { data?: { detail?: unknown } }; message?: unknown } | null;
  const detail = maybe?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (typeof maybe?.message === 'string' && maybe.message.trim()) return maybe.message;
  return fallback;
}

/** Where an outbound Slack message really went, read from its delivery
 *  receipt (the medium's keys sit one level down, under raw_payload.raw_payload):
 *  the monitor group's room with N monitors in it, or a 1:1 DM that holds the
 *  person and the bot only. Null for anything that is not an outbound Slack
 *  send, or for a receipt written before the room kind was recorded. */
export function slackRoomLabel(
  message: Pick<ChannelMessageRead, 'direction' | 'raw_payload'>,
  channel: string,
): string | null {
  if (channel !== 'slack' || message.direction !== 'outbound') return null;
  const receipt = message.raw_payload?.raw_payload;
  if (!receipt || typeof receipt !== 'object') return null;
  const r = receipt as { group_dm?: unknown; monitor_user_ids?: unknown; room_kind?: unknown };
  const groupDm = typeof r.group_dm === 'string' && r.group_dm.trim() ? r.group_dm.trim() : null;
  const monitors = Array.isArray(r.monitor_user_ids) ? r.monitor_user_ids.length : 0;
  // A D… id is a 1:1 im whatever the receipt says: receipts written before
  // kanban 56388550's fix recorded a legacy DM thread as the group room with
  // the monitors listed, and nobody but the person was in it.
  if (groupDm && !/^d/i.test(groupDm)) {
    return monitors > 0
      ? `group DM ${groupDm} · ${monitors} monitor${monitors === 1 ? '' : 's'} in the room`
      : `group DM ${groupDm}`;
  }
  if (r.room_kind === 'dm' || 'group_dm' in r) return '1:1 DM · no monitors in the room';
  return null;
}
